import React from 'react';
import { Box } from '@mui/material';
import { getLoggedUserData, isFeatureEnabled, retrieveConfigParameter } from 'utils';
import { useGetAllowedValues, useGetConfigParameter } from 'hooks';
import { ILoggedUserData } from 'types';
import { Loading } from 'components';

import {
  BoxOption,
  ConsentManagement,
  DateFormatSelector,
  FiscalMonthSelector,
  ClearEnvironmentButton,
  ABBYYSkills,
  EmissionFactorSettings,
  CustomSiteTypes,
  ThresholdValues,
  SwitchSelector,
} from '../components';

export const UserPreferencePage = () => {
  const loggedUserData: ILoggedUserData = getLoggedUserData();

  const { data: configParameter } = useGetConfigParameter();
  const { data: allowedValues } = useGetAllowedValues();

  const isSuperAdminUser = loggedUserData.roles?.includes("SUPER_ADMIN");
  const isAdminUser = isSuperAdminUser || loggedUserData.roles?.includes("MODULE_ADMIN");

  const customSiteType = isFeatureEnabled(configParameter, "custom_site_type_toggle");

  const reviewData = retrieveConfigParameter(configParameter, "needs_review_flag");
  const secondApprovalData = retrieveConfigParameter(configParameter, "second_layer_approval_feature");
  const additionalStatuses = retrieveConfigParameter(configParameter, "unusable_status_enabled");
  const siteBasedData = retrieveConfigParameter(configParameter, "site_based_feature");
  const targetSettingData = retrieveConfigParameter(configParameter, "target_setting_feature");
  const initiativesManagementData = retrieveConfigParameter(configParameter, "initiative_managament_feature");
  // const initiativesModelingData = retrieveConfigParameter(configParameter, "initiative_modeling_feature");

  if (!configParameter || !allowedValues) return <Loading />;

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      mb: 4,
    }}>
      {/* Normal user access */}
      <BoxOption
        title="Date"
        description="Customize your date format preferences."
      >
        <DateFormatSelector loggedUserData={loggedUserData} />
      </BoxOption>
      <BoxOption
        title="Consent Management"
        description="Customize your consent management preferences."
      >
        <ConsentManagement />
      </BoxOption>
      <BoxOption
        title="Fiscal Year Start Month"
        description="Customize your fiscal year start month."
      >
        <FiscalMonthSelector configParameter={configParameter} />
      </BoxOption>

      {/* Super Admin user access */}
      {isSuperAdminUser && (
        <>
          <BoxOption
            title="Clear Environment"
            description="Customize your consent management preferences."
          >
            <ClearEnvironmentButton loggedUserData={loggedUserData} />
          </BoxOption>
          <BoxOption
            title="Status Settings"
            description="Add 'Not Applicable' statuses on the Uploads page."
          >
            <SwitchSelector
              title='"Not Applicable" Statuses Visible'
              requesteData={additionalStatuses}
            />
          </BoxOption>
          <BoxOption
            title="ABBYY Skills"
            description="Customize your fiscal year start month."
          >
            <ABBYYSkills isAdminUser={isAdminUser ?? false} />
          </BoxOption>
          <BoxOption
            title="Emissions Factors Settings"
            description="Customize your consent management preferences."
          >
            <EmissionFactorSettings />
          </BoxOption>
          {/* customSiteType ON */}
          {isAdminUser && customSiteType && (
            <BoxOption
              title="Custom Site Type"
              description="Customize your site type preferences."
              sx={{ justifyContent: "flex-start" }}
            >
              <CustomSiteTypes allowedValues={allowedValues} />
            </BoxOption>
          )}
          <BoxOption
            title="Threshold Value by Utility Type and Site"
            description="Customize threshold value by Utility type and Site."
          >
            <ThresholdValues allowedValues={allowedValues} />
          </BoxOption>
          <BoxOption
            title="IDP Needs Review"
            description="Customize whether bills processed through IDP should always require review."
          >
            <SwitchSelector
              title="Always Set to Needs Review"
              requesteData={reviewData}
            />
          </BoxOption>
          <BoxOption
            title="Second Approval"
            description="Enable &quot;second approval&quot;"
          >
            <SwitchSelector
              title="Enable Second Approval"
              requesteData={secondApprovalData}
            />
          </BoxOption>
          <BoxOption
            title="Site-Based"
            description="Enable Site-Based"
          >
            <SwitchSelector
              title="Enable Site-Based"
              requesteData={siteBasedData}
            />
          </BoxOption>
          <BoxOption
            title="Targets Settings"
            description="Enable Targets Setting"
          >
            <SwitchSelector
              title="Enable Targets Setting"
              requesteData={targetSettingData}
            />
          </BoxOption>
          <BoxOption
            title="Initiatives Management"
            description="Enable Initiatives Management"
          >
            <SwitchSelector
              title="Enable Initiatives Management"
              requesteData={initiativesManagementData}
            />
          </BoxOption>
          {/* <BoxOption
            title="Initiatives Modeling"
            description="Enable Initiatives Modeling"
          >
            <SwitchSelector
              title="Enable Initiatives Modeling"
              requesteData={initiativesModelingData}
            />
          </BoxOption> */}
        </>
      )}
    </Box>
  )
};
